import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Eye, MousePointerClick, DollarSign, 
  Plus, BarChart3, Clock, CheckCircle, XCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdsOverviewPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    running: 0,
    pending: 0,
    totalSpend: 0,
    totalImpressions: 0,
    totalClicks: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/user/campaigns/my-campaigns`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
        
        // Calculate stats
        const stats = data.campaigns.reduce((acc, campaign) => {
          acc.total++;
          if (campaign.status === 'running') acc.running++;
          if (campaign.status === 'pending_approval') acc.pending++;
          acc.totalSpend += campaign.total_cost || 0;
          acc.totalImpressions += campaign.impressions || 0;
          acc.totalClicks += campaign.clicks || 0;
          return acc;
        }, { total: 0, running: 0, pending: 0, totalSpend: 0, totalImpressions: 0, totalClicks: 0 });
        
        setStats(stats);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      running: 'bg-green-100 text-green-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
      paused: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'running') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'rejected') return <XCircle className="h-5 w-5 text-red-600" />;
    if (status === 'pending_approval') return <Clock className="h-5 w-5 text-yellow-600" />;
    return null;
  };

  return (
    <FocusedLayout>
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Matrise Ads</h1>
            <p className="text-muted-foreground">Advertising & Promotions Platform</p>
          </div>
          <Button onClick={() => navigate('/discover/mini-apps/matrise-ads/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Campaigns</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Running</p>
                  <p className="text-2xl font-bold text-green-600">{stats.running}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Spend</p>
                  <p className="text-2xl font-bold">{stats.totalSpend.toLocaleString()} MTR</p>
                </div>
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Impressions</p>
                  <p className="text-2xl font-bold">{stats.totalImpressions.toLocaleString()}</p>
                </div>
                <Eye className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => navigate('/discover/mini-apps/matrise-ads/create')}>
            <CardContent className="pt-6 text-center">
              <Plus className="h-12 w-12 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold mb-1">Create Campaign</h3>
              <p className="text-sm text-muted-foreground">Launch your first ad</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => navigate('/discover/mini-apps/matrise-ads/campaigns')}>
            <CardContent className="pt-6 text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 text-green-600" />
              <h3 className="font-semibold mb-1">My Campaigns</h3>
              <p className="text-sm text-muted-foreground">Manage all campaigns</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 text-purple-600" />
              <h3 className="font-semibold mb-1">Analytics</h3>
              <p className="text-sm text-muted-foreground">View performance</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Campaigns */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No campaigns yet</p>
                <Button onClick={() => navigate('/discover/mini-apps/matrise-ads/create')}>
                  Create Your First Campaign
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.slice(0, 5).map((campaign) => (
                  <div 
                    key={campaign.id} 
                    className="border rounded-lg p-4 hover:bg-muted/50 transition cursor-pointer"
                    onClick={() => navigate(`/discover/mini-apps/matrise-ads/campaigns/${campaign.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getStatusIcon(campaign.status)}
                        <div className="flex-1">
                          <div className="font-semibold">{campaign.campaign_name}</div>
                          <div className="text-sm text-muted-foreground">{campaign.business_name}</div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {campaign.impressions || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <MousePointerClick className="h-4 w-4" />
                              {campaign.clicks || 0}
                            </span>
                            <span className="text-muted-foreground">
                              CTR: {campaign.ctr?.toFixed(2) || 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(campaign.status)}>
                          {campaign.status.replace('_', ' ')}
                        </Badge>
                        <div className="text-sm text-muted-foreground mt-2">
                          {campaign.placement}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3">Advertising Rates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Offers</div>
                <div className="text-xl font-bold">10,000 MTR/day</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Marketplace</div>
                <div className="text-xl font-bold">8,000 MTR/day</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Investments</div>
                <div className="text-xl font-bold">12,000 MTR/day</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </FocusedLayout>
  );
};
